import React, { useCallback, useEffect, useRef, useState } from 'react';
import { adminAPI, AdvanceHistory, AdvanceRequest } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

const money = (value: number) => value.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const errorMessage = (error: any) => typeof error?.response?.data?.detail === 'string'
    ? error.response.data.detail : 'Не удалось выполнить запрос. Повторите попытку.';
const pendingKey = (id: string) => `merchant-advance-pending:${id}`;
type PendingAdvance = AdvanceRequest & { kind?: 'issue' | 'return' };
const savedRequest = (id: string): PendingAdvance | null => {
    try { return JSON.parse(localStorage.getItem(pendingKey(id)) || 'null'); }
    catch { return null; }
};

export const MerchantAdvancePanel: React.FC<{ merchantId: string; canManage: boolean }> = ({ merchantId, canManage }) => {
    const [history, setHistory] = useState<AdvanceHistory | null>(null);
    const [page, setPage] = useState(1);
    const [open, setOpen] = useState(false);
    const [amount, setAmount] = useState('');
    const [comment, setComment] = useState('');
    const [merchantComment, setMerchantComment] = useState('');
    const [pending, setPending] = useState<PendingAdvance | null>(() => savedRequest(merchantId));
    const [confirming, setConfirming] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const [limitDraft, setLimitDraft] = useState('');
    const submitting = useRef(false);
    const load = useCallback(async () => {
        try { setHistory(await adminAPI.getAdvances(merchantId, page)); }
        catch (err) { setError(errorMessage(err)); }
    }, [merchantId, page]);
    useEffect(() => { void load(); }, [load]);
    const normalized = amount.trim().replace('−', '-').replace(',', '.');
    const kind = Number(normalized) < 0 ? 'issue' : 'return';
    const unsignedAmount = normalized.replace(/^[+-]/, '');
    const outstanding = history?.advance?.outstanding ?? history?.advance?.total_issued ?? 0;
    const valid = /^[+-]?\d+(?:\.\d{1,6})?$/.test(normalized) && Number(unsignedAmount) > 0 && Number(unsignedAmount) < 1e9;
    const issue = async () => {
        if (submitting.current || !canManage || (!pending && !valid)) return;
        submitting.current = true;
        setBusy(true); setError(''); setNotice('');
        const request = pending || { kind, amount: unsignedAmount, comment, merchant_comment: merchantComment, request_id: crypto.randomUUID() };
        try {
            // Preserve the same request after timeouts, reloads, or an unknown server outcome.
            localStorage.setItem(pendingKey(merchantId), JSON.stringify(request));
            setPending(request);
            const { kind: operation = 'issue', ...payload } = request;
            await (operation === 'return' ? adminAPI.returnAdvance : adminAPI.issueAdvance)(merchantId, payload);
            localStorage.removeItem(pendingKey(merchantId));
            setPending(null); setOpen(false); setConfirming(false); setAmount(''); setComment(''); setMerchantComment('');
            setNotice(request.kind === 'return' ? 'Поступление от мерчанта зарегистрировано.' : 'Перевод мерчанту зарегистрирован.');
            setPage(1); await load();
        } catch (err: any) {
            setError(errorMessage(err));
            if ([400, 409, 422].includes(err?.response?.status)) {
                localStorage.removeItem(pendingKey(merchantId)); setPending(null); setConfirming(false);
                await load();
            }
        }
        finally { submitting.current = false; setBusy(false); }
    };
    const payoutLimit = history?.payout_limit;
    useEffect(() => { setLimitDraft(String(payoutLimit?.limit ?? 0)); }, [payoutLimit?.limit, payoutLimit?.version]);
    const normalizedLimit = limitDraft.trim().replace(',', '.');
    const validLimit = /^\d+(?:\.\d{1,6})?$/.test(normalizedLimit) && Number(normalizedLimit) < 1e9;
    const saveLimit = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!canManage || !history || !validLimit || submitting.current) return;
        submitting.current = true; setBusy(true); setError(''); setNotice('');
        try {
            const result = await adminAPI.setMerchantPayoutLimit(merchantId, {
                limit: normalizedLimit, expected_version: payoutLimit?.version ?? 0,
            });
            setHistory(previous => previous ? { ...previous, payout_limit: result } : previous);
            setNotice('Лимит выплат сохранён.');
        } catch (err) { setError(errorMessage(err)); await load(); }
        finally { submitting.current = false; setBusy(false); }
    };
    const advance = history?.advance;
    return <section className="mx-6 mb-4 rounded border p-4 space-y-3" aria-label="Переводы между мерчантом (ручные)">
        {error && <p role="alert" className="text-red-600">{error}</p>}
        {notice && <p role="status">{notice}</p>}
        {advance && <>
            <p>Общий баланс мерчанта: <strong>{money(advance.balance)} USDT</strong></p>
            <p>Переводы мерчанту (ручные): {money(advance.total_issued)} USDT</p>
            <p>Поступления от мерчанта (ручные): {money(advance.total_returned ?? 0)} USDT</p>
            <p>Сальдо переводов: <strong>{money(-outstanding)} USDT</strong></p>
            <p className="text-sm text-slate-500">Сеттлы недоступны после начала ручных переводов. Ручные переводы не изменяют доступный баланс для выплат.</p>
        </>}
        {history && <div className="rounded border p-3 space-y-2" aria-label="Лимит выплат">
            <p className="font-medium">Лимит выплат сверх баланса, USDT</p>
            <p className="text-sm">Лимит: {money(payoutLimit?.limit ?? 0)} USDT · Доступно для выплат: {money(payoutLimit?.available ?? 0)} USDT</p>
            <p className="text-xs text-slate-500 dark:text-slate-400">Только для администраторов. Выплаты с комиссией могут использовать этот лимит; депозиты и отмены выплат восстанавливают доступную сумму. 0 — выплаты только из баланса.</p>
            {canManage && <form onSubmit={saveLimit} className="flex flex-wrap items-end gap-2">
                <label className="text-sm">Новый лимит, USDT
                    <Input inputMode="decimal" value={limitDraft} onChange={event => setLimitDraft(event.target.value)} disabled={busy} required />
                </label>
                <Button type="submit" variant="outline" disabled={busy || !validLimit}>Сохранить лимит</Button>
            </form>}
        </div>}
        <div className="flex gap-2">
            {canManage && !open && !pending && <Button variant="outline" className="h-auto whitespace-normal text-left" disabled={!history || busy} onClick={() => { setOpen(true); setNotice(''); }}>Переводы между мерчантом (ручные)</Button>}
        </div>
        {canManage && pending && <div className="space-y-2">
            <p>Запрос {pending.kind === 'return' ? 'получения от мерчанта' : 'отправки мерчанту'} {money(Number(pending.amount))} USDT ожидает подтверждения результата. Повторная отправка не создаст повторную операцию.</p>
            <Button disabled={busy} onClick={() => void issue()}>Повторить запрос</Button>
        </div>}
        {canManage && open && !pending && <form className="space-y-3" onSubmit={event => { event.preventDefault(); if (valid) setConfirming(true); }}>
            <label className="block">Сумма, USDT<Input type="text" placeholder="−20000 или +20000" value={amount} disabled={busy || confirming}
                onChange={event => setAmount(event.target.value)} required /></label>
            <label className="block">Внутренний комментарий<Input value={comment} maxLength={1000} disabled={busy || confirming}
                onChange={event => setComment(event.target.value)} /></label>
            <label className="block">Комментарий для мерчанта<Input value={merchantComment} maxLength={1000} disabled={busy || confirming}
                onChange={event => setMerchantComment(event.target.value)} /></label>
            <p className="text-sm">Минус — отправили мерчанту (−20000), плюс — получили от мерчанта (+20000). Указывается уже выполненный перевод.</p>
            {confirming ? <div className="space-y-2">
                <p>Зарегистрировать {kind === 'return' ? 'поступление от мерчанта' : 'перевод мерчанту'} на {money(Number(unsignedAmount))} USDT?
                    Сальдо переводов станет {money(-outstanding + Number(normalized))} USDT.</p>
                <p>Обычные сеттлы этому мерчанту будут недоступны после начала ручных переводов.</p>
                <Button type="button" disabled={busy} onClick={() => void issue()}>Подтвердить перевод</Button>
                <Button type="button" variant="outline" disabled={busy} onClick={() => setConfirming(false)}>Назад</Button>
            </div> : <Button type="submit" disabled={!valid || busy}>Отправить/получить</Button>}
            <Button type="button" variant="outline" disabled={busy} onClick={() => { setOpen(false); setConfirming(false); }}>Отмена</Button>
        </form>}
        {!!history?.total && <div className="space-y-2">
            <h4 className="font-medium">Журнал ручных переводов</h4>
            <div className="overflow-x-auto"><table className="w-full text-left text-sm">
                <thead><tr><th>Дата</th><th>Операция</th><th>Изменение баланса, USDT</th><th>Сальдо переводов</th><th>Администратор</th><th>Внутренний комментарий</th><th>Комментарий для мерчанта</th></tr></thead>
                <tbody>{history.items.map(item => <tr key={item._id}>
                    <td className="py-2">{new Date(/(?:Z|[+-]\d\d:\d\d)$/.test(item.created_at) ? item.created_at : `${item.created_at}Z`).toLocaleString('ru-RU')}</td>
                    <td>{item.kind === 'return' ? 'Поступление от мерчанта' : 'Перевод мерчанту'}</td>
                    <td>{item.kind === 'return' ? '+' : '−'}{money(item.amount)}</td>
                    <td>{item.outstanding_after !== undefined ? money(-item.outstanding_after) : '—'}</td><td>{item.admin_username}</td><td className="break-words">{item.comment || '—'}</td>
                    <td className="break-words">{item.merchant_comment || '—'}</td>
                </tr>)}</tbody>
            </table></div>
            <div className="flex gap-2 items-center">
                <Button variant="outline" disabled={busy || page <= 1} onClick={() => setPage(page - 1)}>Назад</Button>
                <span>{history.page} / {history.pages}</span>
                <Button variant="outline" disabled={busy || page >= history.pages} onClick={() => setPage(page + 1)}>Далее</Button>
            </div>
        </div>}
    </section>;
};
